import { Datepicker } from "flowbite-react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import StatisticsCards from "../../externalComponents/StatisticsCards";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../externalComponents/tailwind/table";
import { Text } from "../../externalComponents/tailwind/text";
import { useIsMobile } from "../../externalComponents/useIsMobile";
import { calculateRate } from "../../functions/calculateRate";
import { fetchAnalyticsStats } from "../../functions/fetchAnalyticsStats";
import { fetchCampaignAnalyticsStats } from "../../functions/fetchCampaignAnalyticsStats";
import { getAnalyticsDateRange } from "../../functions/getAnalyticsDateRange";
import { CustomerContext } from "../../providers";

export default function StatisticsPage() {
  const { user, integrations, shop } = useContext(CustomerContext);
  const [dataStats, setDataStats] = useState(null);
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDateFilter, setSelectedDateFilter] = useState("30days");
  const today = new Date();
  const [startDate, setStartDate] = useState(() => {
    const _startOfDay = new Date();
    _startOfDay.setUTCHours(0, 0, 0, 0);
    return _startOfDay;
  });
  const [endDate, setEndDate] = useState(() => {
    const _endOfDay = new Date();
    _endOfDay.setUTCHours(23, 59, 59, 999);
    return _endOfDay;
  });

  const [, setStartDateExport] = useState(startDate);
  const [, setEndDateExport] = useState(endDate);

  useEffect(() => {
    if (!user?.customerId) {
      return;
    }

    let isCancelled = false;
    setDataStats({});
    setIsLoading(true);

    const fetchStats = async () => {
      try {
        await fetchAnalyticsStats({
          customerId: user.customerId,
          shopId: shop?.id,
          selectedDateFilter,
          startDate,
          endDate,
          onUpdate: (updater) => {
            if (!isCancelled) {
              setDataStats(updater);
            }
          },
        });
      } catch (error) {
        console.error("Error fetching analytics stats:", error);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      isCancelled = true;
    };
  }, [selectedDateFilter, startDate, endDate, user?.customerId, shop?.id]);

  const newStatsRef = useRef({});

  useEffect(() => {
    let isCancelled = false;
    newStatsRef.current = {};

    const fetchStats = async () => {
      try {
        await fetchAnalyticsStats({
          customerId: user.customerId,
          shopId: shop?.id,
          selectedDateFilter,
          startDate,
          endDate,
          onUpdate: (updater) => {
            if (!isCancelled) {
              newStatsRef.current = updater(newStatsRef.current);
            }
          },
        });
      } catch (error) {
        console.error("[NEW ANALYTICS] Error:", error);
      }
    };

    fetchStats();

    return () => {
      isCancelled = true;
    };
  }, [selectedDateFilter, startDate, endDate, user.customerId, shop?.id]);

  const handleSelectChange = useCallback((value) => {
    setSelectedDateFilter(value);
  }, []);
  const options = [
    { label: "Today", value: "Today" },
    { label: "This week", value: "ThisWeek" },
    { label: "This month", value: "ThisMonth" },
    { label: "Last 7 days", value: "7days" },
    { label: "Last month", value: "LastMonth" },
    { label: "Last 30 days", value: "30days" },
    { label: "Last 90 days", value: "90days" },
    { label: "This year", value: "ThisYear" },
    { label: "Last year", value: "LastYear" },
    { label: "Between", value: "Between" },
  ];

  const customTheme = {
    popup: {
      root: {
        base: isMobile
          ? "fixed left-1/2 top-1/2 z-90 block pt-2 w-[90vw] max-w-xs -translate-x-1/2 -translate-y-1/2"
          : "absolute top-10 z-50 block pt-2 w-[22em]",
        inner:
          "inline-block rounded-customBorderRadius bg-white p-4 border w-full mb-5 dark:bg-gray-800",
        input: {
          field: {
            input: {
              base: "hover:border-yellow ring-purple focus:ring-transparent",
            },
          },
        },
      },
    },
    views: {
      days: {
        items: {
          base: "grid w-full grid-cols-7",
          item: {
            base: "block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600",
            selected: "bg-yellow hover:bg-yellow",
            disabled: "text-gray-400",
          },
        },
      },
    },
  };

  const handleDateChange = (date, type, isExport) => {
    if (type === "start") {
      if (isExport) {
        setStartDateExport(date);
      } else {
        setStartDate(date);
        setStartDateExport(date);
      }
    } else if (type === "end") {
      if (isExport) {
        setEndDateExport(date);
      } else {
        const adjustedEndDate = new Date(date);
        adjustedEndDate.setHours(23, 59, 59, 999);
        setEndDate(adjustedEndDate);
        setEndDateExport(adjustedEndDate);
      }
    }
  };

  return (
    <>
      <PageHeading
        title="Analytics"
        select={true}
        isSelectComponentsDisabled={isLoading}
        selectComponents={{
          options: options,
          selected: selectedDateFilter,
          onChange: (value) => handleSelectChange(value),
          value: selectedDateFilter,
        }}
      />

      <OverflowFrame>
        {selectedDateFilter === "Between" && (
          <div className="flex mt-4 mx-3 md-mx-0">
            <div className="w-[22em]">
              <Datepicker
                disabled={isLoading}
                theme={customTheme}
                maxDate={today}
                value={startDate}
                onChange={(date) => handleDateChange(date, "start", false)}
                weekStart={1}
                showClearButton={false}
                showTodayButton={false}
              />
            </div>
            <div className="w-[22em] ml-3">
              <Datepicker
                disabled={isLoading}
                theme={customTheme}
                maxDate={today}
                minDate={startDate}
                value={endDate}
                onChange={(date) => handleDateChange(date, "end", false)}
                weekStart={1}
                showClearButton={false}
                showTodayButton={false}
              />
            </div>
          </div>
        )}

        <PageMarkup
          data={dataStats}
          user={user}
          shop={shop}
          integrations={integrations}
          selectedDateFilter={selectedDateFilter}
          startDate={startDate}
          endDate={endDate}
          isMobile={isMobile}
        />
      </OverflowFrame>
    </>
  );
}

function PageMarkup({
  data,
  user,
  shop,
  integrations,
  selectedDateFilter,
  startDate,
  endDate,
  isMobile,
}) {
  const [fetchingCampaigns, setFetchingCampaigns] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState({});

  // Fetch campaigns list (without stats)
  useEffect(() => {
    // Skip campaign queries when in Between mode
    if (selectedDateFilter === "Between") {
      setCampaigns([]);
      setAutomations([]);
      setFetchingCampaigns(false);
      return;
    }

    async function fetchAllCampaigns() {
      setFetchingCampaigns(true);
      setCampaignStats({});

      try {
        const dateRange = getAnalyticsDateRange(
          selectedDateFilter,
          startDate,
          endDate,
        );

        const campaignIdsWithMetrics = await api.getCampaignIdsWithMetrics({
          from: dateRange.from,
          to: dateRange.to,
          customerId: user.customer.id,
        });

        const validCampaignIds = campaignIdsWithMetrics.messages
          .map((m) => m.campaignId)
          .filter((id) => id != null);

        if (validCampaignIds.length === 0) {
          setCampaigns([]);
          setAutomations([]);
          setFetchingCampaigns(false);
          return;
        }

        let dataCampaignsTemp = await api.campaign.findMany({
          filter: {
            customerId: { equals: user.customer.id },
            id: { in: validCampaignIds },
          },
          select: {
            id: true,
            name: true,
            automationType: true,
            createdAt: true,
          },
          sort: { createdAt: "Descending" },
        });

        let dataCampaigns = [...dataCampaignsTemp];
        while (dataCampaignsTemp.hasNextPage) {
          dataCampaignsTemp = await dataCampaignsTemp.nextPage();
          dataCampaigns = dataCampaigns.concat(dataCampaignsTemp);
        }

        // Separate automations and campaigns
        const automationsList = dataCampaigns.filter((c) => c.automationType);
        const campaignsList = dataCampaigns.filter((c) => !c.automationType);

        setCampaigns(campaignsList);
        setAutomations(automationsList);
        setFetchingCampaigns(false);

        const fetchCampaignStatsSequentially = async () => {
          const automationsList = dataCampaigns.filter((c) => c.automationType);
          const campaignsList = dataCampaigns.filter((c) => !c.automationType);

          for (const campaign of automationsList) {
            await fetchCampaignAnalyticsStats({
              customerId: user.customer.id,
              shopId: shop?.id,
              campaignId: campaign.id,
              selectedDateFilter,
              startDate,
              endDate,
              onUpdate: (updater) => {
                setCampaignStats((prev) => ({
                  ...prev,
                  [campaign.id]: updater(prev[campaign.id] || {}),
                }));
              },
            });
          }

          for (const campaign of campaignsList) {
            await fetchCampaignAnalyticsStats({
              customerId: user.customer.id,
              shopId: shop?.id,
              campaignId: campaign.id,
              selectedDateFilter,
              startDate,
              endDate,
              onUpdate: (updater) => {
                setCampaignStats((prev) => ({
                  ...prev,
                  [campaign.id]: updater(prev[campaign.id] || {}),
                }));
              },
            });
          }
        };

        fetchCampaignStatsSequentially();
      } catch (error) {
        console.error("Erreur lors de la récupération des campagnes :", error);
        setFetchingCampaigns(false);
      }
    }

    fetchAllCampaigns();
  }, [selectedDateFilter, user.customer.id, shop?.id, startDate, endDate]);

  let timeFilterSuffix = selectedDateFilter;

  let headings = ["Name", "Sent", "Open rate", "Optout rate"];
  if (isMobile) {
    headings = ["Name", "Sent", "ARPC"];
  } else {
    if (integrations.splio || integrations.shopify) {
      headings.splice(3, 0, "Conversion rate");
      headings.splice(3, 0, "ARPC");
      headings.splice(3, 0, "Turnover");
    }
    if (integrations.shopify) {
      headings.splice(3, 0, "Sales");
    }
  }

  const newOptins = data?.[`optins${timeFilterSuffix}`] || "-";

  const sentRate = data
    ? calculateRate(
        data[`sent${timeFilterSuffix}`],
        data[`messagesCount${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const deliveredRate = data
    ? calculateRate(
        data[`delivered${timeFilterSuffix}`],
        data[`sent${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const openRate = data
    ? calculateRate(
        data[`read${timeFilterSuffix}`],
        data[`delivered${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const failedRate = data
    ? calculateRate(
        data[`messagesCount${timeFilterSuffix}`] -
          data[`sent${timeFilterSuffix}`],
        data[`messagesCount${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const optoutRate = data
    ? calculateRate(
        data[`optoutCount${timeFilterSuffix}`],
        data[`sent${timeFilterSuffix}`],
      ).toFixed(1)
    : null;

  const turnover =
    user.customer.isExternalTurnoverSplioActivated &&
    data?.[`externalTurnover${timeFilterSuffix}`]
      ? data[`externalTurnover${timeFilterSuffix}`].toFixed(0)
      : integrations.shopify && data?.[`turnover${timeFilterSuffix}`]
        ? data[`turnover${timeFilterSuffix}`].toFixed(0)
        : 0;

  const conversionRate = (
    Math.min(
      1,
      data?.[`sales${timeFilterSuffix}`] /
        Math.max(1, data?.[`messagesCount${timeFilterSuffix}`]),
    ) * 100
  )?.toFixed(1);

  const revenueStats = [
    {
      name: "Sales",
      tooltip: true,
      tooltipContent: "Number of sales attributed to WhatsApp.",
      value: !data
        ? null
        : data[`sales${timeFilterSuffix}`] > 0
          ? data[`sales${timeFilterSuffix}`]
          : "0",
    },
    {
      name: "Turnover",
      tooltip: true,
      tooltipContent: "The total revenue attributed to WhatsApp.",
      value: !data ? null : turnover ? `${turnover}€` : "-",
    },
    {
      name: "WhatsApp cost",
      tooltip: true,
      tooltipContent:
        "The WhatsApp cost is available for messages sent after May 17th, 2025.",
      value: !data
        ? null
        : data[`totalWhatsappCostInEuros${timeFilterSuffix}`] > 0
          ? `${(data[`totalWhatsappCostInEuros${timeFilterSuffix}`] || 0).toFixed(2)}€`
          : "0€",
    },
    {
      name: "Conversion rate",
      tooltip: true,
      tooltipContent: "Percentage of messages that resulted in a sale.",
      value: !data
        ? null
        : data[`sales${timeFilterSuffix}`] > 0
          ? conversionRate + "%"
          : "-",
    },
    {
      name: "ARPC",
      tooltip: true,
      tooltipContent:
        "Average Revenue Per Conversation: turnover divided by sent messages.",
      value: !data
        ? null
        : data[`turnover${timeFilterSuffix}`] && data[`sent${timeFilterSuffix}`]
          ? (
              data[`turnover${timeFilterSuffix}`] /
              data[`sent${timeFilterSuffix}`]
            ).toFixed(2) + "€"
          : data[`externalTurnover${timeFilterSuffix}`] > 0
            ? (
                data[`externalTurnover${timeFilterSuffix}`] /
                data[`delivered${timeFilterSuffix}`]
              ).toFixed(2) + "€"
            : "0€",
    },
    {
      name: "ROI",
      tooltip: true,
      tooltipContent:
        "Turnover divided by WhatsApp cost. The WhatsApp cost is available for messages sent after May 17th, 2025.",
      value: !data
        ? null
        : data[`totalWhatsappCostInEuros${timeFilterSuffix}`] > 0
          ? (
              turnover / data[`totalWhatsappCostInEuros${timeFilterSuffix}`]
            ).toFixed(2)
          : "-",
    },
  ];
  const audienceStats = [
    {
      name: "Total optins",
      value: user.customer.optinsCount,
    },
    {
      name: "New optins",
      value: !data ? null : newOptins,
    },
    {
      name: "Optouts",
      value: !data ? null : data?.[`optoutCount${timeFilterSuffix}`],
    },
    {
      name: "Optout rate",
      value: !data ? null : optoutRate > 0 ? optoutRate + "%" : "-",
      change: !data
        ? null
        : `${data?.[`optoutCount${timeFilterSuffix}`] || 0}/${
            data?.[`sent${timeFilterSuffix}`] || 0
          }`,
      // value: !data ? null : `${optoutRate}%`,
    },
  ];

  const sendingsStats = [
    {
      name: "Sent rate",
      value: !data ? null : sentRate > 0 ? sentRate + "%" : "-",
      change: !data
        ? null
        : `${data?.[`sent${timeFilterSuffix}`] || 0}/${
            data?.[`messagesCount${timeFilterSuffix}`] || 0
          }`,
    },
    {
      name: "Delivered rate",
      value: !data ? null : deliveredRate > 0 ? deliveredRate + "%" : "-",
      change: !data
        ? null
        : `${data?.[`delivered${timeFilterSuffix}`] || 0}/${
            data?.[`sent${timeFilterSuffix}`] || 0
          }`,
    },
    {
      name: "Open rate",
      value: !data ? null : openRate > 0 ? openRate + "%" : "-",
      change: !data
        ? null
        : `${data?.[`read${timeFilterSuffix}`] || 0}/${
            data?.[`delivered${timeFilterSuffix}`] || 0
          }`,
    },
    {
      name: "Failed rate",
      value: !data ? null : failedRate > 0 ? failedRate + "%" : "-",
      change: !data
        ? null
        : `${
            data?.[`messagesCount${timeFilterSuffix}`] -
              data?.[`sent${timeFilterSuffix}`] || 0
          }/${data?.[`messagesCount${timeFilterSuffix}`] || 0}`,
    },
  ];

  return (
    <>
      {(integrations.shopify || integrations.splio) && (
        <div className="border bg-greyDashboardBG rounded-customBorderRadius p-3 my-4 mx-3 md:mx-0">
          <Text className="text-xl font-bold mb-5">Revenue</Text>
          <StatisticsCards stats={revenueStats} />
        </div>
      )}

      <div className="border bg-greyDashboardBG rounded-customBorderRadius p-3 my-4 mx-3 md:mx-0">
        <Text className="text-xl font-bold mb-5">Audience</Text>
        <StatisticsCards stats={audienceStats} />
      </div>
      <div className="border bg-greyDashboardBG border-pink rounded-customBorderRadius p-3 my-4 mx-3 md:mx-0">
        <Text className="text-xl font-bold mb-5">
          {`Deliverability on `}
          <span className="text-[#DF0F63] text-xl">
            {data?.[`messagesCount${timeFilterSuffix}`] || "0"}
          </span>
          {` targeted recipients`}
          {!!data?.totalWhatsappCostInEuros && (
            <>
              {" "}
              for{" "}
              <span className="text-[#DF0F63] text-xl">
                {data.totalWhatsappCostInEuros.toFixed(2) + "€"}
              </span>
            </>
          )}
        </Text>
        <StatisticsCards stats={sendingsStats} />
      </div>

      {selectedDateFilter !== "Between" && (
        <>
          <div className="flex-col border rounded-customBorderRadius p-3 mt-3 mx-3 md:mx-0">
            <Text className="text-lg font-bold">Automations</Text>
            {!fetchingCampaigns ? (
              <Table>
                <TableHead>
                  <TableRow>
                    {headings.map((heading, index) => (
                      <TableHeader
                        key={index}
                        className={index > 0 ? "text-center" : "w-[20%]"}
                      >
                        {heading}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                {automations && (
                  <TableBody>
                    <DataRows
                      data={automations}
                      campaignStats={campaignStats}
                      user={user}
                      integrations={integrations}
                      timeFilterSuffix={timeFilterSuffix}
                      isMobile={isMobile}
                    />
                  </TableBody>
                )}
              </Table>
            ) : (
              <div className="h-[50px]">
                <LoadingSpinner />
              </div>
            )}
          </div>
          <br />
          <div className="flex-col border rounded-customBorderRadius p-3 mt-3 mx-3 md:mx-0">
            <Text className="text-lg font-bold">Campaigns</Text>

            {!fetchingCampaigns ? (
              <Table>
                <TableHead>
                  <TableRow>
                    {headings.map((heading, index) => (
                      <TableHeader
                        key={index}
                        className={index > 0 ? "text-center" : "w-[20%]"}
                      >
                        {heading}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                {campaigns && (
                  <TableBody>
                    <DataRows
                      data={campaigns}
                      campaignStats={campaignStats}
                      user={user}
                      integrations={integrations}
                      timeFilterSuffix={timeFilterSuffix}
                      isMobile={isMobile}
                    />
                  </TableBody>
                )}
              </Table>
            ) : (
              <div className="h-[100px]">
                <LoadingSpinner />
              </div>
            )}
          </div>
        </>
      )}

      <br />
      <br />
    </>
  );
}

function DataRows({
  data,
  campaignStats,
  user,
  integrations,
  timeFilterSuffix,
  isMobile,
}) {
  // Build rows from data and campaignStats (streaming)
  const rows = (data || [])
    .map((campaign) => {
      const stats = campaignStats[campaign.id] || {};

      return (
        <TableRow key={campaign.id} href={`/analytics/${campaign.id}`}>
          <TableCell>{campaign.name}</TableCell>
          <TableCell className="text-center">
            {stats?.[`sent${timeFilterSuffix}`] || "-"}
          </TableCell>
          {!isMobile && (
            <>
              <TableCell className="text-center">
                {stats?.[`delivered${timeFilterSuffix}`]
                  ? (
                      Math.min(
                        1,
                        (stats[`read${timeFilterSuffix}`] || 0) /
                          Math.max(1, stats[`delivered${timeFilterSuffix}`]),
                      ) * 100
                    ).toFixed(0) + "%"
                  : "-"}
              </TableCell>

              {integrations.shopify && (
                <>
                  <TableCell className="text-center">
                    {stats?.[`sales${timeFilterSuffix}`] || "-"}
                  </TableCell>
                  <TableCell className="text-center">
                    {stats?.[`turnover${timeFilterSuffix}`] > 0
                      ? `${stats[`turnover${timeFilterSuffix}`].toFixed(2)} €`
                      : "-"}
                  </TableCell>
                  <TableCell className="text-center">
                    {stats?.[`turnover${timeFilterSuffix}`] > 0
                      ? `${(
                          stats[`turnover${timeFilterSuffix}`] /
                          Math.max(stats[`sent${timeFilterSuffix}`], 1)
                        ).toFixed(2)} €`
                      : "-"}
                  </TableCell>
                </>
              )}
            </>
          )}

          {user.customer.isExternalTurnoverSplioActivated && !isMobile && (
            <>
              <TableCell className="text-center">
                {stats?.[`externalTurnover${timeFilterSuffix}`] > 0
                  ? stats[`externalTurnover${timeFilterSuffix}`] + "€"
                  : "-"}
              </TableCell>
              <TableCell className="text-center">
                {stats?.[`externalTurnover${timeFilterSuffix}`] > 0
                  ? (
                      stats[`externalTurnover${timeFilterSuffix}`] /
                      Math.max(1, stats[`sent${timeFilterSuffix}`])
                    ).toFixed(2) + "€"
                  : "-"}
              </TableCell>
            </>
          )}

          {!isMobile && (
            <>
              {integrations.shopify && (
                <TableCell className="text-center">
                  {stats?.[`sent${timeFilterSuffix}`]
                    ? `${(
                        Math.min(
                          1,
                          (stats[`sales${timeFilterSuffix}`] || 0) /
                            Math.max(1, stats[`sent${timeFilterSuffix}`]),
                        ) * 100
                      ).toFixed(1)}%`
                    : "-"}
                </TableCell>
              )}

              <TableCell className="text-center">
                {stats?.[`sent${timeFilterSuffix}`]
                  ? `${(
                      Math.min(
                        1,
                        (stats[`optoutCount${timeFilterSuffix}`] || 0) /
                          Math.max(1, stats[`sent${timeFilterSuffix}`]),
                      ) * 100
                    ).toFixed(1)}%`
                  : "-"}
              </TableCell>
            </>
          )}
        </TableRow>
      );
    })
    .filter(Boolean)
    .sort((a, b) => {
      const sentA = campaignStats[a.key]?.[`sent${timeFilterSuffix}`] || 0;
      const sentB = campaignStats[b.key]?.[`sent${timeFilterSuffix}`] || 0;
      return sentB - sentA;
    });

  return <>{rows}</>;
}
